import React, { useState, useEffect, useCallback } from 'react';
import { RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useLanguage } from '@/contexts/LanguageContext';

export interface ImageAdjustments {
  brightness: number;
  contrast: number;
  saturation: number;
}

export const defaultAdjustments: ImageAdjustments = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
};

interface AdjustmentsPanelProps {
  adjustments: ImageAdjustments;
  onAdjustmentsChange: (adjustments: ImageAdjustments) => void;
  onReset: () => void;
}

const AdjustmentsPanel: React.FC<AdjustmentsPanelProps> = ({
  adjustments,
  onAdjustmentsChange,
  onReset,
}) => {
  const { t } = useLanguage();
  
  // Local state for immediate UI feedback
  const [localAdjustments, setLocalAdjustments] = useState(adjustments);

  // Sync local state when parent changes (e.g., reset)
  useEffect(() => {
    setLocalAdjustments(adjustments);
  }, [adjustments]);

  // Debounced update to parent
  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        localAdjustments.brightness !== adjustments.brightness ||
        localAdjustments.contrast !== adjustments.contrast ||
        localAdjustments.saturation !== adjustments.saturation
      ) {
        onAdjustmentsChange(localAdjustments);
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [localAdjustments, adjustments, onAdjustmentsChange]);

  const handleChange = useCallback((key: keyof ImageAdjustments, value: number) => {
    setLocalAdjustments(prev => ({ ...prev, [key]: value }));
  }, []);

  const isDefault = 
    localAdjustments.brightness === 100 && 
    localAdjustments.contrast === 100 && 
    localAdjustments.saturation === 100;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="panel-title font-semibold text-foreground">{t.imageEnhancement}</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          disabled={isDefault}
          className="h-7 px-2 text-xs"
          aria-label="Reset adjustments"
        >
          <RotateCcw className="w-3 h-3 mr-1" />
          {t.resetAdjustments}
        </Button>
      </div>
      
      {/* Brightness */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="panel-label text-muted-foreground">{t.brightness}</label>
          <span className="panel-value font-medium text-foreground w-12 text-right">
            {localAdjustments.brightness}%
          </span>
        </div>
        <Slider
          value={[localAdjustments.brightness]}
          onValueChange={([v]) => handleChange('brightness', v)}
          min={0}
          max={200}
          step={5}
          className="w-full"
        />
      </div>

      {/* Contrast */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="panel-label text-muted-foreground">{t.contrast}</label>
          <span className="panel-value font-medium text-foreground w-12 text-right">
            {localAdjustments.contrast}%
          </span>
        </div>
        <Slider
          value={[localAdjustments.contrast]}
          onValueChange={([v]) => handleChange('contrast', v)}
          min={0}
          max={200}
          step={5}
          className="w-full"
        />
      </div>

      {/* Saturation */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="panel-label text-muted-foreground">{t.saturation}</label>
          <span className="panel-value font-medium text-foreground w-12 text-right">
            {localAdjustments.saturation}%
          </span>
        </div>
        <Slider
          value={[localAdjustments.saturation]}
          onValueChange={([v]) => handleChange('saturation', v)}
          min={0}
          max={200}
          step={5}
          className="w-full"
        />
      </div>

      {/* Preview hint */}
      <p className="panel-label text-muted-foreground text-center pt-2">
        {t.adjustmentsApplyToAll}
      </p>
    </div>
  );
};

export default AdjustmentsPanel;
